import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { loadDraftFromStorage } from '../useClaimState';
import api from '@/utils/api';
import { useEmailNotification } from '../useEmailNotification';
import type { ClaimState, ClaimDispatch, PhaseId } from '../types';

interface UseEmailFlowParams {
  state: ClaimState;
  dispatch: ClaimDispatch;
  goToPhase: (phase: PhaseId) => void;
  setExtractedData: (data: any) => void;
  resumeCaseId: string | undefined;
  navigate: (options: any) => void;
  hasLoadedDraft: React.MutableRefObject<boolean>;
}

/**
 * Email flow hook for new claim flow.
 * Handles:
 * - Pending token initialization and validation
 * - Token expiry checking
 * - Email notification WebSocket connection
 * - Extracted data from inbound emails
 */
export function useEmailFlow({
  state,
  dispatch,
  goToPhase,
  setExtractedData,
  resumeCaseId,
  navigate,
  hasLoadedDraft,
}: UseEmailFlowParams) {
  const hasInitialized = useRef(false);

  // Email notification hook (WebSocket for inbound email)
  const emailNotification = useEmailNotification(state.caseId);

  // Handle email notification: extracted data from inbound email
  useEffect(() => {
    if (emailNotification.extractedData && state.phase === 'awaiting_email') {
      // Email extraction completed - apply the extracted data
      setExtractedData(emailNotification.extractedData);
      goToPhase('booking_review');
      hasInitialized.current = true;
      emailNotification.disconnect();
      toast.success('Booking details extracted from email!');
    }
  }, [emailNotification.extractedData, state.phase, setExtractedData, goToPhase, emailNotification]);

  // Connect to email WebSocket when resuming awaiting_email phase
  useEffect(() => {
    if (
      state.phase === 'awaiting_email' &&
      state.caseId &&
      emailNotification.status === 'disconnected' &&
      !emailNotification.extractedData
    ) {
      emailNotification.connect();
    }
  }, [state.phase, state.caseId, emailNotification]);

  // Get or validate pending email token on mount
  // This provides an email address without creating a Case
  useEffect(() => {
    const initializeToken = async () => {
      // Skip if resuming an existing case or already have a case
      if (resumeCaseId || state.caseId || hasLoadedDraft.current) {
        return;
      }

      // Check localStorage first - if there's a draft with a caseId, don't create token
      const localDraft = loadDraftFromStorage();
      if (localDraft?.caseId) {
        return;
      }

      // Check if we have a pending token in localStorage/state
      const existingToken = localDraft?.pendingToken || state.pendingToken;

      if (existingToken) {
        // Validate the existing token
        try {
          const response = await api.get(`/api/cases/pending-token/${existingToken}/validate/`);

          if (response.data.status === 'claimed') {
            // Token was claimed (email arrived) - redirect to resume that case
            navigate({
              to: '/client/new-claim',
              search: { resumeCase: response.data.case_id },
              replace: true,
            });
            return;
          }

          if (response.data.status === 'active') {
            // Token still valid - use it
            dispatch({
              type: 'SET_PENDING_TOKEN',
              token: existingToken,
              emailAddress: response.data.email_address,
              expiresAt: response.data.expires_at,
            });
            return;
          }

          // Token expired or invalid - will get a new one below
        } catch (err) {
          console.error('Failed to validate pending token:', err);
          // Will get a new token below
        }
      }

      // Get a new pending token
      try {
        const response = await api.post('/api/cases/pending-token/');
        dispatch({
          type: 'SET_PENDING_TOKEN',
          token: response.data.token,
          emailAddress: response.data.email_address,
          expiresAt: response.data.expires_at,
        });
      } catch (err) {
        console.error('Failed to get pending token:', err);
        // Non-fatal - user can still upload files, just won't have email option ready
      }
    };

    initializeToken();
  }, [resumeCaseId, state.caseId, state.pendingToken, dispatch, navigate, hasLoadedDraft]);

  // Check token expiry periodically
  useEffect(() => {
    // Only check if we have a token and no case yet
    if (!state.tokenExpiresAt || state.caseId) return;

    const checkExpiry = () => {
      const expiresAt = new Date(state.tokenExpiresAt!);
      if (new Date() > expiresAt) {
        dispatch({ type: 'SET_TOKEN_EXPIRED', expired: true });
      }
    };

    checkExpiry(); // Check immediately
    const interval = setInterval(checkExpiry, 30000); // Then every 30s

    return () => clearInterval(interval);
  }, [state.tokenExpiresAt, state.caseId, dispatch]);

  return {
    emailNotification,
    hasInitialized,
  };
}
